import React, { useEffect, useRef, useState } from 'react';
import { CustomComponentEditor } from '../custom-component/CustomComponentEditor';
import { Formik, Form, Field } from 'formik';
import FormFieldWithLabel from '../../common/FormFieldWithLabel';
import { drapcodeIcon } from '../../../common/custom/icon-library';
import Swal from 'sweetalert2';
import ParentCardNoCol from '../../common/ParentCardNoCol';
import { Link } from 'react-router-dom';
import { CustomFunctionEditor } from './CustomFunctionEditor';

export const CustomFunctionEditForm = ({
  customFunctionId,
  projectId,
  version,
  collections,
  pages,
  events,
  actions,
}) => {
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [functionCode, setFunctionCode] = useState('');
  const [executionOutput, setExecutionOutput] = useState('');
  const [executing, setExecuting] = useState(false);
  const formikValuesRef = useRef({ name: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomFunction = async () => {
      try {
        const response = await actions.loadCustomFunctionDetail({ projectId, customFunctionId });
        const data = response.data;
        const name = data.name || '';
        setInitialFormValues({ name });
        formikValuesRef.current.name = name;
        setFunctionCode(data.functionCode || '');
        setLoading(false);
        console.log('Fetched custom function:', data);
      } catch (error) {
        console.error('Error fetching custom function:', error);
        setLoading(false);
      }
    };

    if (customFunctionId) {
      fetchCustomFunction();
    } else {
      setLoading(false);
    }
  }, [customFunctionId, projectId, actions]);

  const handleFunctionCodeSubmit = async () => {
    const updatedData = {
      projectId,
      version,
      uuid: customFunctionId,
      functionCode,
    };
    try {
      await actions.updateCustomFunction({
        projectId,
        customFunctionId,
        version,
        updatedData,
      });
      await Swal.fire('Success', 'Function saved successfully!', 'success');
    } catch (error) {
      console.error('Error updating function code:', error);
      await Swal.fire('Error', 'Failed to save function', 'error');
    }
  };

  const handleMetaSubmit = async (values) => {
    const updatedData = {
      projectId,
      version,
      uuid: customFunctionId,
      name: values.name,
    };
    try {
      await actions.updateCustomFunction({
        projectId,
        customFunctionId,
        version,
        updatedData,
      });
      await Swal.fire('Success', 'Function saved successfully!', 'success');
    } catch (error) {
      console.error('Error updating function meta:', error);
      await Swal.fire('Error', 'Failed to save function', 'error');
    }
  };

  const handleExecuteFunction = async () => {
    setExecuting(true);
    try {
      const payload = {
        uuid: customFunctionId,
        projectId,
        version,
        functionCode,
        name: formikValuesRef.current.name,
      };
      const response = await actions.customFunctionExecution({
        projectId,
        customFunctionId,
        payload,
      });
      console.log('This is the response data after the final api call: ', response.data);
      setExecutionOutput(JSON.stringify(response.data, null, 2));
    } catch (error) {
      console.error('Function execution error:', error);
      console.log('This is the error message ', error.message);
      setExecutionOutput(`Execution Failed: ${error}`);
    } finally {
      setExecuting(false);
    }
  };

  const handleLogsFunction = async () => {
    try {
      const response = await actions.customFunctionLogs({ projectId, customFunctionId });
      console.log('Logs response:', response.data);
      setExecutionOutput(JSON.stringify(response.data, null, 2));
    } catch (error) {
      console.error('Logs fetch error:', error);
      setExecutionOutput(`Fetching Logs Failed: ${error}`);
    }
  };

  return (
    <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12 drapcode">
      <ParentCardNoCol title="Custom Function" icon="customFunction" bgColor="drapcode-violet">
        {initialFormValues ? (
          <Formik enableReinitialize initialValues={initialFormValues} onSubmit={handleMetaSubmit}>
            {({ values }) => {
              formikValuesRef.current = values;
              return (
                <Form className="form-horizontal">
                  <div className="row mx-auto">
                    <div className="col-md-12">
                      <FormFieldWithLabel label="Function Name" icon="text">
                        <Field type="text" name="name" className="form-control form-control-sm" />
                      </FormFieldWithLabel>
                    </div>
                  </div>
                  <div className="col-md-12 mt-2 text-right">
                    <button type="submit" className="btn btn-theme-submit btn-md">
                      <i className={`wb ${drapcodeIcon('update')}`}></i>&nbsp;Update
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        ) : (
          <div>Error loading function details.</div>
        )}
      </ParentCardNoCol>

      <CustomComponentEditor
        title="Function Code"
        icon="customFunction"
        value={functionCode}
        mode="javascript"
        collections={collections}
        pages={pages}
        events={events}
        showDynamicOptions={false}
        onChange={setFunctionCode}
        onSubmit={handleFunctionCodeSubmit}
      />

      <div className="col-md-12 mt-3 text-right">
        <button
          onClick={handleExecuteFunction}
          className="btn btn-theme-submit btn-md"
          disabled={executing}
        >
          <i className={`wb ${drapcodeIcon('play')}`}></i>&nbsp;
          {executing ? 'Deploying...' : 'Deploy'}
        </button>

        <Link
          className="btn btn-theme-primary btn-md"
          title={`Show Logs`}
          to={{
            pathname: `/custom-component/${version}/v/${projectId}/customFunction/logs/${customFunctionId}`,
          }}
        >
          <i className="wb webconnect-file-text"></i>&nbsp; Show Logs
        </Link>
      </div>

      <CustomFunctionEditor
        title="Execution Output"
        icon="terminal"
        value={executionOutput}
        mode="json"
        // height="500px"
      />
    </div>
  );
};
